/**
 * Client for the profile schedule write path (issue #257, extended by #297):
 * `PATCH /v1/guest/me` (guest tier) and `PATCH /v1/me` (authed tier), both
 * backed by the same `patchMeCore` (backend/src/api/index.ts), used by the
 * onboarding ExamDatePicker/TrainingTimePicker/GoalSetup screens and the
 * post-onboarding GoalEditor to persist the exam date, training-time window,
 * deadline stance, and daily study intent. Each field is independently
 * optional in the request body, mirroring the backend's write semantics.
 */
import { authConfig, type AuthConfig } from '@/lib/auth/config';
import { authedFetch, guestFetch, type FetchImpl } from '@/lib/auth/api';
import type { AwsCredentials } from '@/lib/auth/types';

export type { FetchImpl };

/** Implementation-intention training window (backend `TrainingTime`, on-wire shape). */
export interface TrainingTime {
  hour: number;
  weekdays: number[];
}

/** Explicit deadline stance (backend `DeadlineStance`, issue #295). */
export type DeadlineStance = 'has_date' | 'no_date';

/** The exact `PATCH /v1/guest/me` / `PATCH /v1/me` request body — every field independently optional. */
export interface PatchMeBody {
  exam_date?: string | null;
  training_time?: TrainingTime | null;
  deadline_stance?: DeadlineStance | null;
  new_atoms_per_day?: number | null;
}

/** The response — echoes back only the fields that were set. */
export interface PatchMeResponse {
  uid: string;
  exam_date?: string | null;
  training_time?: TrainingTime | null;
  deadline_stance?: DeadlineStance | null;
  new_atoms_per_day?: number | null;
}

/** Thrown on any non-2xx response, carrying the status and the backend's error code. */
export class ProfileApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
  ) {
    super(`Profile API error ${status}: ${code}`);
    this.name = 'ProfileApiError';
  }
}

async function errorCodeFrom(response: Response): Promise<string> {
  try {
    const body = (await response.json()) as { error?: unknown };
    return typeof body.error === 'string' ? body.error : 'unknown_error';
  } catch {
    return 'unknown_error';
  }
}

/** SigV4-sign and PATCH the guest profile schedule; throws `ProfileApiError` on a non-2xx. */
export async function patchGuestMe(
  credentials: AwsCredentials,
  body: PatchMeBody,
  config: AuthConfig = authConfig,
  fetchImpl: FetchImpl = fetch,
): Promise<PatchMeResponse> {
  const response = await guestFetch(
    credentials,
    '/v1/guest/me',
    {
      method: 'PATCH',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body),
    },
    config,
    fetchImpl,
  );
  if (!response.ok) {
    throw new ProfileApiError(response.status, await errorCodeFrom(response));
  }
  return (await response.json()) as PatchMeResponse;
}

/** Bearer-authed PATCH of the signed-in caller's profile schedule; throws `ProfileApiError` on a non-2xx. */
export async function patchMe(
  idToken: string,
  identityId: string,
  body: PatchMeBody,
  config: AuthConfig = authConfig,
  fetchImpl: FetchImpl = fetch,
): Promise<PatchMeResponse> {
  const response = await authedFetch(
    {
      idToken,
      identityId,
      path: '/v1/me',
      init: {
        method: 'PATCH',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(body),
      },
    },
    config,
    fetchImpl,
  );
  if (!response.ok) {
    throw new ProfileApiError(response.status, await errorCodeFrom(response));
  }
  return (await response.json()) as PatchMeResponse;
}
